import React, { useState, useEffect, useMemo } from 'react';
import { FormMetadata, FormVersion, FieldType, SignatureMethod } from '../../types';
import SignatureInput from './SignatureInput';
import { db } from '@/src/services/db';
import { saveDraft, getDraft, deleteDraft, enqueueSubmission } from '@/src/services/formStorage';
import { generateResponseHash } from '@/src/utils/crypto';
import { useAuth } from '@/src/context/AuthContext';
import { MapPin, Upload, Calendar, AlertCircle, Save, CheckCircle2, ChevronLeft, ArrowRight, X, Trash2, Plus, Star, CheckSquare, CircleDot } from 'lucide-react';

interface FormResponderProps {
  formId: string;
  onClose: () => void;
}

const FormResponder: React.FC<FormResponderProps> = ({ formId, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState<FormMetadata | null>(null);
  const [activeVersion, setActiveVersion] = useState<FormVersion | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionStart, setSessionStart] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  // Geolocation State
  const [locationAuthorized, setLocationAuthorized] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{lat: number, lng: number} | null>(null);

  useEffect(() => {
    // Start Session Timer
    setSessionStart(new Date().toISOString());

    db.getFormById(formId).then(f => {
      if (f) {
        setForm(f);
        const version = f.versions.find(v => v.version === f.currentVersion);
        setActiveVersion(version || null);
      }
      setLoading(false);
    });

    // Restore Draft
    getDraft(formId).then(data => {
        if (data && Object.keys(data).length > 0) {
            console.log(`[FormResponder] Restored draft for ${formId}`);
            setFormData(data);
        }
    });
  }, [formId]);

  // Handle Location Check
  useEffect(() => {
    if (!form) return;

    if (!form.requireLocation) {
        setLocationAuthorized(true);
        return;
    }

    const cachedAuth = localStorage.getItem('asa_branca_location_authorized');
    if (cachedAuth === 'true' && !locationAuthorized) {
        requestLocation(); // Silently request to get coords
    }
  }, [form, locationAuthorized]);

  const requestLocation = () => {
      setLocationError(null);
      if (!navigator.geolocation) {
          setLocationError("Geolocalização não suportada pelo seu navegador.");
          return;
      }

      navigator.geolocation.getCurrentPosition(
          (position) => {
              setLocationAuthorized(true);
              setCurrentLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
              localStorage.setItem('asa_branca_location_authorized', 'true');
          },
          (error) => {
              console.error("Location error:", error);
              if (error.code === error.PERMISSION_DENIED) {
                  setLocationError("Permissão negada. Por favor, libere a localização nas configurações do seu navegador e tente novamente.");
                  localStorage.removeItem('asa_branca_location_authorized');
              } else {
                  setLocationError("Não foi possível obter sua localização. Tente novamente.");
              }
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
  };

  // Auto-Save Draft
  useEffect(() => {
    const handler = setTimeout(() => {
        if (Object.keys(formData).length > 0) {
            saveDraft(formId, formData);
        }
    }, 500); // Debounce 500ms

    return () => clearTimeout(handler);
  }, [formData, formId]);

  const handleFieldChange = (fieldId: string, value: any, metadata?: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: { value, metadata }
    }));
  };

  const handleCheckboxChange = (fieldId: string, value: string) => {
      const currentValues = (formData[fieldId]?.value as string[]) || [];
      let newValues;
      if (currentValues.includes(value)) {
          newValues = currentValues.filter(v => v !== value);
      } else {
          newValues = [...currentValues, value];
      }
      handleFieldChange(fieldId, newValues.length > 0 ? newValues : null);
  };

  // Matrix Logic
  const handleMatrixChange = (fieldId: string, rowIndex: number, colId: string, value: any) => {
      const currentRows = (formData[fieldId]?.value as any[]) || [];
      const newRows = [...currentRows];
      if (!newRows[rowIndex]) newRows[rowIndex] = {};
      newRows[rowIndex][colId] = value;
      handleFieldChange(fieldId, newRows);
  };

  const addMatrixRow = (fieldId: string) => {
      const currentRows = (formData[fieldId]?.value as any[]) || [];
      handleFieldChange(fieldId, [...currentRows, {}]);
  };

  const removeMatrixRow = (fieldId: string, index: number) => {
      const currentRows = (formData[fieldId]?.value as any[]) || [];
      const newRows = [...currentRows];
      newRows.splice(index, 1);
      handleFieldChange(fieldId, newRows);
  };

  const handleFileUpload = (fieldId: string, e: React.ChangeEvent<HTMLInputElement>, allowMultiple: boolean) => {
      const files = e.target.files;
      if (files && files.length > 0) {
          const readers: Promise<string>[] = [];
          
          for (let i = 0; i < files.length; i++) {
              readers.push(new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.readAsDataURL(files[i]);
              }));
          }

          Promise.all(readers).then(results => {
              if (allowMultiple) {
                  const existing = formData[fieldId]?.value || [];
                  const currentArray = Array.isArray(existing) ? existing : [existing].filter(Boolean);
                  handleFieldChange(fieldId, [...currentArray, ...results]);
              } else {
                  handleFieldChange(fieldId, results[0]);
              }
          });
      }
  };

  const removeMediaItem = (fieldId: string, index: number) => {
      const existing = formData[fieldId]?.value;
      if (Array.isArray(existing)) {
          const newArray = [...existing];
          newArray.splice(index, 1);
          handleFieldChange(fieldId, newArray.length > 0 ? newArray : null);
      } else {
          handleFieldChange(fieldId, null);
      }
  };

  const handleRefusal = (fieldId: string, reason: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: { 
        value: 'REFUSED', 
        metadata: { 
            refusalReason: reason,
            timestamp: new Date().toISOString()
        } 
      }
    }));
  };

  const validateForm = useMemo(() => {
    if (!activeVersion) return { isValid: false, missing: [] };
    const missing: string[] = [];
    
    activeVersion.schema.forEach(field => {
        const val = formData[field.id]?.value;
        if (field.required) {
            if (val === undefined || val === null || val === '') {
                missing.push(field.id);
            }
            if (Array.isArray(val) && val.length === 0) {
                 missing.push(field.id);
            }
        }
    });
    return { isValid: missing.length === 0, missing };
  }, [formData, activeVersion]);

  // Calculate Progress
  const progress = useMemo(() => {
    if (!activeVersion) return 0;
    const total = activeVersion.schema.length;
    if (total === 0) return 100;
    const filled = activeVersion.schema.filter(f => {
        const val = formData[f.id]?.value;
        if (Array.isArray(val)) return val.length > 0;
        return val !== undefined && val !== null && val !== '';
    }).length;
    return Math.round((filled / total) * 100);
  }, [formData, activeVersion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !activeVersion) return;
    if (!validateForm.isValid) return; // Poka-Yoke
    if (!user) {
        alert("Sessão expirada. Faça login novamente.");
        return;
    }
    
    setSubmitting(true);

    const timestamp = new Date().toISOString();
    let hash = '';

    try {
        hash = await generateResponseHash(user.id, timestamp, formData);
    } catch (e) {
        console.error("Hash generation failed", e);
        alert("Erro ao gerar assinatura digital.");
        setSubmitting(false);
        return;
    }

    const payload = {
        formId: form.id,
        formVersion: form.currentVersion,
        respondentId: user.id,
        respondentName: user.name,
        status: 'submitted' as const,
        startedAt: sessionStart || timestamp,
        data: formData,
        deviceInfo: {
            geolocation: currentLocation || undefined
        },
        securityMetadata: {
            hash,
            algorithm: 'SHA-256',
            timestamp
        }
    };

    try {
        const res = await db.submitResponse(payload, user);
        await deleteDraft(formId);
        setSubmittedId(res.id);
    } catch (err) {
        console.error("Online submission failed, attempting queue...", err);
        try {
            await enqueueSubmission(payload);
            await deleteDraft(formId);
            alert("Sem conexão com o servidor. O formulário foi salvo na Fila de Saída e será enviado automaticamente.");
            onClose();
        } catch (queueErr) {
            console.error(queueErr);
            alert("ERRO CRÍTICO: Falha no envio e no salvamento local. Tente novamente.");
            setSubmitting(false);
        }
    }
  };

  // --- Success Screen (Receipt) ---
  if (submittedId && form) {
    return (
        <div className="fixed inset-0 bg-slate-50 z-50 flex flex-col items-center justify-center p-6">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center space-y-6 animate-in zoom-in-95 duration-300">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600">
                    <CheckCircle2 size={48} />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Envio Recebido</h2>
                    <p className="text-slate-500 mt-2">Seus dados foram criptografados e armazenados com segurança.</p>
                </div>
                
                <div className="bg-slate-50 p-4 rounded-lg text-left text-sm space-y-2 border border-slate-100">
                    <div className="flex justify-between">
                        <span className="text-slate-500">ID:</span>
                        <span className="font-mono text-slate-700">{submittedId.split('-')[1]}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Formulário:</span>
                        <span className="font-medium text-slate-700 truncate max-w-[150px]">{form.title}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Horário:</span>
                        <span className="text-slate-700">{new Date().toLocaleTimeString()}</span>
                    </div>
                </div>

                <button 
                    onClick={onClose} 
                    className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold text-lg hover:bg-slate-800 transition-colors"
                >
                    Concluído
                </button>
            </div>
        </div>
    );
  }

  if (loading) return <div className="p-10 text-center flex items-center justify-center h-full text-slate-500">Carregando espaço de trabalho...</div>;
  if (!form || !activeVersion) return <div className="p-10 text-center text-red-500">Definição do formulário não encontrada.</div>;

  const requiresAuthOverlay = form.requireLocation && !locationAuthorized;

  return (
    <div className="bg-slate-100 min-h-screen flex justify-center relative">
      {requiresAuthOverlay && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-6">
              <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Localização Necessária</h3>
                  <p className="text-sm text-slate-600">Este formulário exige o registro da sua localização no momento do preenchimento.</p>

                  {locationError ? (
                      <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
                          {locationError}
                      </div>
                  ) : null}

                  <button
                      onClick={requestLocation}
                      className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-sm"
                  >
                      Autorizar Localização
                  </button>
                  <button
                      onClick={onClose}
                      className="w-full py-2 text-slate-500 font-medium hover:text-slate-700"
                  >
                      Cancelar
                  </button>
              </div>
          </div>
      )}

      {/* Mobile Container Simulation */}
      <div className={`w-full max-w-md bg-white min-h-screen shadow-2xl flex flex-col relative ${requiresAuthOverlay ? 'pointer-events-none' : ''}`}>
        
        {/* Sticky Header with Progress */}
        <div className="sticky top-0 bg-white z-20 border-b border-slate-100 shadow-sm">
            <div className="px-4 py-3 flex items-center gap-3">
                <button onClick={onClose} className="p-2 -ml-2 text-slate-400 hover:text-slate-700">
                    <ChevronLeft size={24} />
                </button>
                <div className="flex-1">
                    <h1 className="text-sm font-bold text-slate-800 truncate">{form.title}</h1>
                    <p className="text-xs text-slate-400">{form.sector}</p>
                </div>
                <div className="text-xs font-bold text-indigo-600">{progress}%</div>
            </div>
            {/* Progress Bar */}
            <div className="h-1 w-full bg-slate-100">
                <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${progress}%` }}></div>
            </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 p-5 space-y-6 overflow-y-auto pb-32">
            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl text-sm text-blue-800">
                {form.description}
            </div>

            {activeVersion.schema.map(field => {
                const fieldData = formData[field.id] || {};
                const isRefused = fieldData.value === 'REFUSED';

                return (
                    <div key={field.id} className="space-y-2 animate-in slide-in-from-bottom-4 duration-500">
                        {/* Label */}
                        {!isRefused && field.type !== FieldType.SIGNATURE && (
                            <label className="block text-base font-semibold text-slate-800">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                        )}

                        {/* Input Area */}
                        <div className="relative">
                            {isRefused ? (
                                <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex flex-col gap-2">
                                    <div className="text-red-800 font-semibold flex items-center gap-2">
                                        <AlertCircle size={20}/> Assinatura Recusada
                                    </div>
                                    <p className="text-sm text-red-600 italic">"{fieldData.metadata?.refusalReason}"</p>
                                    <button 
                                        type="button" 
                                        onClick={() => handleFieldChange(field.id, null)} 
                                        className="text-sm text-blue-600 underline self-start mt-1"
                                    >
                                        Desfazer Recusa
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* --- Input Types Optimized for Touch --- */}
                                    
                                    {field.type === FieldType.TEXT_SHORT && (
                                        <input 
                                            type="text" 
                                            required={field.required}
                                            className="w-full bg-slate-50 border-0 rounded-xl p-4 text-lg text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Toque para digitar..."
                                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                        />
                                    )}

                                    {field.type === FieldType.TEXT_LONG && (
                                        <textarea 
                                            required={field.required}
                                            rows={4}
                                            className="w-full bg-slate-50 border-0 rounded-xl p-4 text-lg text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Digite uma descrição detalhada..."
                                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                        />
                                    )}

                                    {field.type === FieldType.NUMBER && (
                                        <input 
                                            type="number" 
                                            inputMode="numeric"
                                            required={field.required}
                                            className="w-full bg-slate-50 border-0 rounded-xl p-4 text-2xl font-mono text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                            placeholder="0.00"
                                            onChange={(e) => handleFieldChange(field.id, parseFloat(e.target.value))}
                                        />
                                    )}

                                    {field.type === FieldType.DATE && (
                                        <input 
                                            type="date" 
                                            required={field.required}
                                            className="w-full bg-slate-50 border-0 rounded-xl p-4 text-lg text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                        />
                                    )}

                                    {/* --- NEW FIELDS --- */}

                                    {field.type === FieldType.YES_NO && (
                                        <div className="flex gap-4">
                                            <button
                                                type="button"
                                                onClick={() => handleFieldChange(field.id, 'Sim')}
                                                className={`flex-1 py-4 rounded-xl border-2 font-bold transition-all ${
                                                    fieldData.value === 'Sim' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500'
                                                }`}
                                            >
                                                SIM
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleFieldChange(field.id, 'Não')}
                                                className={`flex-1 py-4 rounded-xl border-2 font-bold transition-all ${
                                                    fieldData.value === 'Não' ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-500'
                                                }`}
                                            >
                                                NÃO
                                            </button>
                                        </div>
                                    )}

                                    {field.type === FieldType.MATRIX && (
                                        <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm text-left">
                                                    <thead className="bg-slate-100 text-slate-600 font-bold uppercase text-xs">
                                                        <tr>
                                                            {field.matrixConfig?.columns.map(col => (
                                                                <th key={col.id} className="px-4 py-3 min-w-[120px]">{col.label}</th>
                                                            ))}
                                                            <th className="px-2 py-3 w-10"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {(fieldData.value as any[] || []).map((row, rIdx) => (
                                                            <tr key={rIdx} className="bg-white">
                                                                {field.matrixConfig?.columns.map(col => (
                                                                    <td key={col.id} className="p-2">
                                                                        {col.type === 'text' && (
                                                                            <input 
                                                                                className="w-full border rounded p-2 text-slate-800 text-base md:text-sm"
                                                                                value={row[col.id] || ''}
                                                                                onChange={e => handleMatrixChange(field.id, rIdx, col.id, e.target.value)}
                                                                            />
                                                                        )}
                                                                        {col.type === 'number' && (
                                                                            <input 
                                                                                type="number"
                                                                                className="w-full border rounded p-2 text-slate-800 text-base md:text-sm"
                                                                                value={row[col.id] || ''}
                                                                                onChange={e => handleMatrixChange(field.id, rIdx, col.id, e.target.value)}
                                                                            />
                                                                        )}
                                                                        {col.type === 'select' && (() => {
                                                                            const firstCol = field.matrixConfig?.columns[0];
                                                                            const shouldFilter = field.matrixConfig?.preventDuplicatesOnFirstColumn
                                                                                && firstCol?.id === col.id;
                                                                            const allRows = (fieldData.value as any[]) || [];
                                                                            const usedValues = shouldFilter
                                                                                ? new Set(
                                                                                    allRows
                                                                                        .filter((_, i) => i !== rIdx)
                                                                                        .map(r => r[col.id])
                                                                                        .filter(Boolean)
                                                                                  )
                                                                                : new Set<string>();
                                                                            return (
                                                                                <select 
                                                                                    className="w-full border rounded p-2 text-slate-800 bg-white text-base md:text-sm"
                                                                                    value={row[col.id] || ''}
                                                                                    onChange={e => handleMatrixChange(field.id, rIdx, col.id, e.target.value)}
                                                                                >
                                                                                    <option value="">...</option>
                                                                                    {col.options
                                                                                        ?.filter(opt => !usedValues.has(opt))
                                                                                        .map(opt => (
                                                                                            <option key={opt} value={opt}>{opt}</option>
                                                                                        ))}
                                                                                </select>
                                                                            );
                                                                        })()}
                                                                    </td>
                                                                ))}
                                                                <td className="p-2 text-center">
                                                                    <button type="button" onClick={() => removeMatrixRow(field.id, rIdx)} className="text-red-400 hover:text-red-600">
                                                                        <Trash2 size={16}/>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={() => addMatrixRow(field.id)}
                                                className="w-full py-3 text-indigo-600 font-bold text-sm bg-slate-50 hover:bg-slate-100 border-t border-slate-200"
                                            >
                                                + Adicionar Linha
                                            </button>
                                        </div>
                                    )}

                                    {field.type === FieldType.RATING && (
                                        <div className="flex justify-center gap-2 py-4">
                                            {[...Array(field.ratingConfig?.maxStars || 5)].map((_, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => handleFieldChange(field.id, i + 1)}
                                                    className={`p-2 transition-transform active:scale-125 ${
                                                        (fieldData.value || 0) > i ? 'text-yellow-400' : 'text-slate-300'
                                                    }`}
                                                >
                                                    <Star size={40} fill="currentColor" strokeWidth={1} />
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {field.type === FieldType.RADIO && (
                                        <div className="space-y-3">
                                            {field.options?.map((opt) => (
                                                <label key={opt.value} className={`
                                                    flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer
                                                    ${fieldData.value === opt.value ? 'border-indigo-500 bg-indigo-50' : 'border-slate-100 bg-slate-50'}
                                                `}>
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${fieldData.value === opt.value ? 'border-indigo-500' : 'border-slate-300'}`}>
                                                        {fieldData.value === opt.value && <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>}
                                                    </div>
                                                    <input 
                                                        type="radio" 
                                                        name={field.id} 
                                                        value={opt.value}
                                                        className="hidden"
                                                        onChange={() => handleFieldChange(field.id, opt.value)}
                                                    />
                                                    <span className={`text-lg font-medium ${fieldData.value === opt.value ? 'text-indigo-900' : 'text-slate-700'}`}>
                                                        {opt.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {field.type === FieldType.CHECKBOX && (
                                        <div className="space-y-3">
                                            {field.options?.map((opt) => {
                                                const isChecked = (fieldData.value || []).includes(opt.value);
                                                return (
                                                    <label key={opt.value} className={`
                                                        flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer
                                                        ${isChecked ? 'border-indigo-500 bg-indigo-50' : 'border-slate-100 bg-slate-50'}
                                                    `}>
                                                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${isChecked ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-slate-300'}`}>
                                                            {isChecked && <CheckCircle2 size={16} />}
                                                        </div>
                                                        <input 
                                                            type="checkbox" 
                                                            value={opt.value}
                                                            className="hidden"
                                                            onChange={() => handleCheckboxChange(field.id, opt.value)}
                                                        />
                                                        <span className={`text-lg font-medium ${isChecked ? 'text-indigo-900' : 'text-slate-700'}`}>
                                                            {opt.label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {field.type === FieldType.LINEAR_SCALE && (
                                        <div className="py-2">
                                            <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase px-1">
                                                <span>{field.linearScaleConfig?.minLabel}</span>
                                                <span>{field.linearScaleConfig?.maxLabel}</span>
                                            </div>
                                            <div className="flex justify-between items-center bg-slate-50 p-2 rounded-xl border border-slate-100">
                                                {Array.from({ length: (field.linearScaleConfig?.max || 5) - (field.linearScaleConfig?.min || 1) + 1 }, (_, i) => {
                                                    const val = (field.linearScaleConfig?.min || 1) + i;
                                                    const isSelected = fieldData.value === val;
                                                    return (
                                                        <button
                                                            key={val}
                                                            type="button"
                                                            onClick={() => handleFieldChange(field.id, val)}
                                                            className={`
                                                                w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all
                                                                ${isSelected ? 'bg-indigo-600 text-white shadow-lg scale-110' : 'text-slate-400 hover:bg-white hover:shadow'}
                                                            `}
                                                        >
                                                            {val}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* --- END NEW FIELDS --- */}

                                    {field.type === FieldType.SELECT && (
                                        <div className="relative">
                                            <select 
                                                required={field.required}
                                                className="w-full bg-slate-50 border-0 rounded-xl p-4 text-lg text-slate-900 focus:ring-2 focus:ring-indigo-500 appearance-none"
                                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                            >
                                                <option value="">Toque para selecionar...</option>
                                                {field.options?.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                                                <ArrowRight size={20} className="rotate-90"/>
                                            </div>
                                        </div>
                                    )}

                                    {field.type === FieldType.MEDIA_PHOTO && (
                                        <div className="space-y-4">
                                            {/* Existing Images Grid */}
                                            {fieldData.value && (Array.isArray(fieldData.value) || typeof fieldData.value === 'string') && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {(Array.isArray(fieldData.value) ? fieldData.value : [fieldData.value]).map((img: string, idx: number) => (
                                                        <div key={idx} className="relative rounded-xl overflow-hidden shadow-sm border border-slate-200 aspect-square">
                                                            <img src={img} alt={`Evidence ${idx}`} className="w-full h-full object-cover" />
                                                            <button 
                                                                type="button"
                                                                onClick={() => removeMediaItem(field.id, idx)}
                                                                className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full text-red-500 shadow-sm"
                                                            >
                                                                <Trash2 size={16}/>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Add Button */}
                                            {(field.mediaConfig?.allowMultiple || !fieldData.value) && (
                                                <label className={`
                                                    border-2 border-dashed border-slate-300 rounded-xl p-6 text-center 
                                                    bg-slate-50 active:bg-slate-100 transition-colors cursor-pointer block
                                                    ${fieldData.value ? 'p-4 flex items-center justify-center gap-2' : ''}
                                                `}>
                                                    {fieldData.value ? (
                                                        <>
                                                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
                                                                <Plus size={20} />
                                                            </div>
                                                            <span className="font-semibold text-slate-600 text-sm">Adicionar Mais</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm text-indigo-500">
                                                                <Upload size={32} />
                                                            </div>
                                                            <span className="font-semibold text-slate-600 block">
                                                                {field.mediaConfig?.allowMultiple ? 'Adicionar Fotos/Vídeos' : 'Tirar Foto'}
                                                            </span>
                                                            <span className="text-xs text-slate-400">ou carregar da galeria</span>
                                                        </>
                                                    )}
                                                    <input 
                                                        type="file" 
                                                        accept="image/*"
                                                        capture="environment"
                                                        multiple={field.mediaConfig?.allowMultiple}
                                                        className="hidden" 
                                                        onChange={(e) => handleFileUpload(field.id, e, field.mediaConfig?.allowMultiple || false)}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    )}

                                    {field.type === FieldType.SIGNATURE && field.signatureConfig && (
                                        <SignatureInput 
                                            label={field.label}
                                            method={field.signatureConfig.method}
                                            allowRefusal={field.signatureConfig.allowRefusal}
                                            value={fieldData.value}
                                            onChange={(val) => handleFieldChange(field.id, val)}
                                            onRefusal={(reason) => handleRefusal(field.id, reason)}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </form>

        {/* Sticky Footer Action */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-100 pb-8">
            <button 
                onClick={handleSubmit}
                disabled={!validateForm.isValid || submitting}
                className={`
                    w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all
                    ${!validateForm.isValid 
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                        : 'bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 active:scale-95'
                    }
                `}
            >
                {submitting ? (
                    'Enviando...' 
                ) : !validateForm.isValid ? (
                    `Complete ${validateForm.missing.length} campos`
                ) : (
                    <>Enviar Relatório <ArrowRight size={20}/></>
                )}
            </button>
        </div>

      </div>
    </div>
  );
};

export default FormResponder;
